import { AxiosError } from "axios"
import clsx from "clsx"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { SpinnerCircular } from "spinners-react"
import {
  CompletedMarkDoubleIcon,
  GoldenDustImg,
  QuestAvatarLabradorImg
} from "@/assets"
import { numberSpaceFixer } from "@/helpers/numberSpaceFixer"
import { openTgLink } from "@/helpers/openTgLink"
import { showToast } from "@/helpers/toast"
import { QuestType, useClaimQuestMutation } from "@/store/apis/questsAPI"
import styles from "./QuestCard.module.scss"
import { IQuestCardProps } from "./types"

type State = "available" | "ready" | "claimed"

const slugifyTitle = (title: string): string =>
  title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

const getClaimErrorKey = (error: unknown): string => {
  const axiosError = error as AxiosError<{ message?: string }>
  const status = axiosError?.response?.status
  const message = axiosError?.response?.data?.message

  if (status === 403 && message === "NOT_SUBSCRIBED_TO_TELEGRAM_CHAT") {
    return "quests.errors.notSubscribed"
  }
  if (status === 503 && message === "TELEGRAM_VERIFICATION_UNAVAILABLE") {
    return "quests.errors.verificationUnavailable"
  }
  if (status === 409 && message === "QUEST_ALREADY_CLAIMED") {
    return "quests.errors.alreadyClaimed"
  }
  return "quests.errors.claimFailed"
}

export const QuestCard: FC<IQuestCardProps> = ({
  quest,
  completed,
  progressMet = false
}) => {
  const { t } = useTranslation()
  const [claimQuest, { isLoading: isClaiming }] = useClaimQuestMutation()
  const [opened, setOpened] = useState<boolean>(false)

  const isLoading = isClaiming
  const isTelegram = quest.type === QuestType.TELEGRAM

  const state: State = completed?.isClaimed
    ? "claimed"
    : (isTelegram ? opened : progressMet)
      ? "ready"
      : "available"

  const handleClick = async () => {
    if (isLoading || state === "claimed") return

    if (state === "available") {
      if (quest.url) openTgLink(quest.url)
      if (isTelegram) setOpened(true)
      return
    }

    if (state === "ready") {
      try {
        await claimQuest(quest._id).unwrap()
      } catch (error) {
        setOpened(false)
        showToast.error(t(getClaimErrorKey(error)))
      }
    }
  }

  return (
    <article
      className={clsx(styles.QuestCard, styles[state], {
        [styles.loading]: isLoading
      })}
      onClick={handleClick}
    >
      {isLoading && (
        <div className={styles.QuestCard_loader}>
          <SpinnerCircular size={36} color="#ffd180" />
        </div>
      )}

      <div className={styles.QuestCard_img}>
        <img
          src={quest.imageUrl ?? QuestAvatarLabradorImg}
          alt=""
          aria-hidden
        />
      </div>

      <div className={styles.QuestCard_info}>
        <div className={styles.QuestCard_title}>
          {t(`quests.titles.${slugifyTitle(quest.title)}`, {
            defaultValue: quest.title
          })}
        </div>

        <div className={styles.QuestCard_bottom}>
          <div className={styles.QuestCard_reward}>
            {numberSpaceFixer(quest.rewardGoldenDust)}
            <img src={GoldenDustImg} alt="" aria-hidden />
          </div>

          {state === "available" && (
            <div className={styles.QuestCard_action}>{t("quests.open")}</div>
          )}
          {state === "ready" && (
            <div className={styles.QuestCard_action}>{t("quests.claim")}</div>
          )}
          {state === "claimed" && (
            <div className={styles.QuestCard_claimed} aria-hidden>
              <CompletedMarkDoubleIcon />
            </div>
          )}
        </div>
      </div>
    </article>
  )
}
